import { Activity } from '@shared/models/design/activities/activity.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { ItemOrItemId, SemiPartial } from '@shared/models/type-helpers.model';
import { ValidatedValue } from '@shared/validation/validation.models';
import { createActivity } from '../factories/activity.factory';
import { getActivityPropertiesWithDefaults } from '../activities.helpers';
import { getId } from '../../object.helpers';
import { guards } from '../../typeguards.helpers';
import { mutateStoreyActivityReference } from './storey-activity-reference.mutator';
import { mapStoreysInDesign } from '../storey.helpers';

const activityTypesWithDefaultProperties = new Set<ActivityType>([
  ActivityType.Residential,
  ActivityType.School,
  ActivityType.Stairwells,
]);

/**
 * Merge property updates and backfill type defaults when a program has defined defaults.
 */
const mergeActivityPropertyUpdate = (
  activity: Activity,
  propertyUpdate: Activity['properties'] | undefined,
): Activity['properties'] => {
  if (!propertyUpdate) return activity.properties;

  const merged = {
    ...activity.properties,
    ...propertyUpdate,
  };

  if (!activityTypesWithDefaultProperties.has(activity.type)) return merged;

  return getActivityPropertiesWithDefaults(activity.type, merged);
};

/**
 * Add a new activity to the building design
 * @param design Building design to add the activity to
 * @param base Partial activity to create
 * @returns Validated item if valid after adding the activity, otherwise the error
 */
const addActivity = (
  design: BuildingDesign,
  base: Parameters<typeof createActivity>[0],
): ValidatedValue<BuildingDesign> => {
  const activities = design.activities;

  // TODO: Handle better than throwing an error
  if (activities.find((activity) => activity.type === base?.type)) {
    throw new Error(`"${base?.type}" activity already exists`);
  }
  const newActivity = createActivity(base);

  if (guards.is.error(newActivity)) {
    return newActivity;
  }

  return {
    ...design,
    activities: [...activities, newActivity],
  };
};

/**
 * Remove an activity and return the updated design
 * @param design Building design to remove the activity from
 * @param activityOrId Activity or id of the activity to remove
 * @returns
 */
const removeActivity = (
  design: BuildingDesign,
  activityOrId: ItemOrItemId<Activity>,
): BuildingDesign => {
  const id = getId(activityOrId);
  const activities = design.activities;

  // TODO: Consider if this is the place to do this.
  // Remove the activity reference from all storeys across every building
  const nextDesign = mapStoreysInDesign(design, (storey) =>
    mutateStoreyActivityReference.remove(storey, id),
  );

  return {
    ...nextDesign,
    activities: activities.filter((activity) => activity.id !== id),
  };
};

const updateActivity = (
  design: BuildingDesign,
  update: SemiPartial<Activity, 'id'>,
): BuildingDesign => {
  const activities = design.activities.map((activity) => {
    if (activity.id !== update.id) return activity;
    // Boverket would be proud of this type-safe update
    return {
      ...activity,
      ...update,
      properties: mergeActivityPropertyUpdate(activity, update.properties),
    } as Activity;
  });

  return {
    ...design,
    activities,
  };
};

/**
 * Helper to mutate a design by adding/removing/updating activities and storeys
 */
export const mutateActivity = {
  add: addActivity,
  remove: removeActivity,
  update: updateActivity,
} as const;
